import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Button,
  Stack,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { APP_STORE_URL, GOOGLE_PLAY_URL } from '../constants/storeLinks';
import {
  APP_STORE_BADGE_SRC,
  GOOGLE_PLAY_BADGE_SRC,
  STORE_MARKETING_BADGE_HEIGHT,
} from '../utils/storeMarketingBadges';
import { apiClient, HOST_RETENTION_EVENTS } from '../publicApiClient';

const SNOOZE_KEY = 'mobile-app-install-snooze-until';
const LEGACY_DISMISS_KEY = 'ios-pwa-install-dismissed';

interface MobileAppInstallDialogProps {
  onClose?: () => void;
}

function isSnoozed(): boolean {
  const raw = localStorage.getItem(SNOOZE_KEY);
  if (!raw) return false;
  const until = Date.parse(raw);
  return !Number.isNaN(until) && until > Date.now();
}

function migrateLegacyDismiss(): void {
  if (localStorage.getItem(SNOOZE_KEY)) return;
  const legacy = localStorage.getItem(LEGACY_DISMISS_KEY);
  if (!legacy) return;
  const t = Date.parse(legacy);
  if (!Number.isNaN(t) && t > Date.now()) {
    localStorage.setItem(SNOOZE_KEY, legacy);
  }
}

function detectMobile(): { isMobile: boolean; isAndroid: boolean } {
  const ua = window.navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isAndroid = /android/.test(ua);
  return { isMobile: isIOS || isAndroid, isAndroid };
}

const MobileAppInstallDialog: React.FC<MobileAppInstallDialogProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    migrateLegacyDismiss();

    const w = window as unknown as {
      showMobileAppInstallPrompt?: () => void;
      showIOSPWAPrompt?: () => void;
    };
    const show = () => {
      localStorage.removeItem(SNOOZE_KEY);
      localStorage.removeItem(LEGACY_DISMISS_KEY);
      setOpen(true);
    };
    w.showMobileAppInstallPrompt = show;
    w.showIOSPWAPrompt = show;

    return () => {
      delete w.showMobileAppInstallPrompt;
      delete w.showIOSPWAPrompt;
    };
  }, []);

  useEffect(() => {
    const { isMobile } = detectMobile();
    const inStandalone =
      ('standalone' in window.navigator &&
        (window.navigator as Navigator & { standalone?: boolean }).standalone) ||
      window.matchMedia('(display-mode: standalone)').matches;

    if (!isMobile || inStandalone || isSnoozed()) {
      return;
    }

    const timer = window.setTimeout(() => setOpen(true), 3000);
    return () => window.clearTimeout(timer);
  }, []);

  const setSnooze = (until: Date) => {
    localStorage.setItem(SNOOZE_KEY, until.toISOString());
    localStorage.removeItem(LEGACY_DISMISS_KEY);
  };

  const handleClose = () => {
    const until = new Date();
    until.setDate(until.getDate() + 30);
    setSnooze(until);
    setOpen(false);
    onClose?.();
  };

  const handleLater = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    setSnooze(d);
    setOpen(false);
    onClose?.();
  };

  const trackDownloadClick = (platform: 'ios' | 'android') => {
    void apiClient.trackHostRetentionEvent(HOST_RETENTION_EVENTS.APP_DOWNLOAD_CLICKED, {
      platform,
      userAgent: window.navigator.userAgent,
    });
  };

  if (!open) return null;

  const { isAndroid } = detectMobile();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('mobileApp.prompt.title')}
          </Typography>
          <IconButton
            aria-label={t('mobileApp.prompt.close')}
            onClick={handleClose}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Typography variant="body1" color="text.secondary">
              {t('mobileApp.prompt.description')}
            </Typography>

            <Stack
              direction="row"
              spacing={2}
              useFlexGap
              flexWrap="wrap"
              justifyContent="center"
              alignItems="center"
            >
              {isAndroid ? (
                <>
                  <Link
                    href={GOOGLE_PLAY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('mobileApp.prompt.googlePlayAlt')}
                    onClick={() => trackDownloadClick('android')}
                    sx={{ lineHeight: 0, display: 'inline-block' }}
                  >
                    <Box
                      component="img"
                      src={GOOGLE_PLAY_BADGE_SRC}
                      alt=""
                      sx={{ height: STORE_MARKETING_BADGE_HEIGHT, width: 'auto', display: 'block' }}
                    />
                  </Link>
                  <Link
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('mobileApp.prompt.appStoreAlt')}
                    onClick={() => trackDownloadClick('ios')}
                    sx={{ lineHeight: 0, display: 'inline-block' }}
                  >
                    <Box
                      component="img"
                      src={APP_STORE_BADGE_SRC}
                      alt=""
                      sx={{ height: STORE_MARKETING_BADGE_HEIGHT, width: 'auto', display: 'block' }}
                    />
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('mobileApp.prompt.appStoreAlt')}
                    onClick={() => trackDownloadClick('ios')}
                    sx={{ lineHeight: 0, display: 'inline-block' }}
                  >
                    <Box
                      component="img"
                      src={APP_STORE_BADGE_SRC}
                      alt=""
                      sx={{ height: STORE_MARKETING_BADGE_HEIGHT, width: 'auto', display: 'block' }}
                    />
                  </Link>
                  <Link
                    href={GOOGLE_PLAY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('mobileApp.prompt.googlePlayAlt')}
                    onClick={() => trackDownloadClick('android')}
                    sx={{ lineHeight: 0, display: 'inline-block' }}
                  >
                    <Box
                      component="img"
                      src={GOOGLE_PLAY_BADGE_SRC}
                      alt=""
                      sx={{ height: STORE_MARKETING_BADGE_HEIGHT, width: 'auto', display: 'block' }}
                    />
                  </Link>
                </>
              )}
            </Stack>

            <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
              <Button variant="outlined" onClick={handleLater} fullWidth>
                {t('mobileApp.prompt.later')}
              </Button>
              <Button variant="contained" onClick={handleClose} fullWidth>
                {t('mobileApp.prompt.gotIt')}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MobileAppInstallDialog;
